/**
 * Sección "Cuentas conectadas" — Google + Microsoft.
 *
 * Cada provider tiene tres estados:
 *   - Conectado y desvinculable   → botón "Desconectar"
 *   - Conectado pero indispensable (cuenta sin password ni otro provider) → botón deshabilitado con tooltip
 *   - No conectado                → botón "Conectar" que arranca el OAuth flow contra prod
 */

import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'

import {
  ActionButton,
  InlineAlert,
  SettingsCard,
  SettingsSection,
} from '@/components/ui/settings-primitives'
import { useDisconnectOAuth, useOAuthAccounts } from '@/hooks/api/use-profile'
import { startOAuth } from '@/lib/oauth'
import type { OAuthAccountInfo, ProfileResponse } from '@/lib/api'

interface ProviderMeta {
  id: 'google' | 'microsoft'
  name: string
  icon: ReactNode
}

const PROVIDERS: ProviderMeta[] = [
  {
    id: 'google',
    name: 'Google',
    icon: (
      <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden>
        <path
          fill="#FFC107"
          d="M43.6 20.5H42V20H24v8h11.3a12 12 0 1 1-3.4-12.7l5.7-5.6A20 20 0 1 0 44 24c0-1.2-.1-2.4-.4-3.5z"
        />
        <path
          fill="#FF3D00"
          d="m6.3 14.7 6.6 4.8C14.7 16 19 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.6A20 20 0 0 0 6.3 14.7z"
        />
        <path
          fill="#4CAF50"
          d="M24 44c5.2 0 10-2 13.5-5.2L31.3 33c-2 1.5-4.5 2.5-7.3 2.5a12 12 0 0 1-11.3-7.9l-6.5 5A20 20 0 0 0 24 44z"
        />
        <path
          fill="#1976D2"
          d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.7 5l6.2 5.2c-.4.4 6.6-4.8 6.6-14.2 0-1.2-.1-2.4-.4-3.5z"
        />
      </svg>
    ),
  },
  {
    id: 'microsoft',
    name: 'Microsoft',
    icon: (
      <svg viewBox="0 0 23 23" className="h-5 w-5" aria-hidden>
        <rect x="1" y="1" width="10" height="10" fill="#F25022" />
        <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
        <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
        <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
      </svg>
    ),
  },
]

export function OAuthSection({ profile }: { profile: ProfileResponse }) {
  const { data, isLoading } = useOAuthAccounts()
  const disconnect = useDisconnectOAuth()
  const [confirming, setConfirming] = useState<'google' | 'microsoft' | null>(null)

  if (isLoading || !data) {
    return (
      <SettingsCard className="p-6">
        <div className="h-32 animate-pulse rounded bg-bg-elevated" />
      </SettingsCard>
    )
  }

  const linked: Record<'google' | 'microsoft', OAuthAccountInfo | undefined> = {
    google: data.accounts.find((a) => a.provider === 'google'),
    microsoft: data.accounts.find((a) => a.provider === 'microsoft'),
  }
  const totalLinked = data.accounts.length

  const canDisconnect = (id: 'google' | 'microsoft') => {
    // The user keeps access if they have a password OR another provider.
    if (profile.has_password) return true
    return totalLinked - (linked[id] ? 1 : 0) >= 1
  }

  const onDisconnect = async (id: 'google' | 'microsoft') => {
    try {
      await disconnect.mutateAsync(id)
      toast.success(`${labelOf(id)} desconectado`)
      setConfirming(null)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'No pudimos desconectar')
    }
  }

  return (
    <SettingsSection
      title="Cuentas conectadas"
      description="Permítele a Google y/o Microsoft que te identifiquen para iniciar sesión sin escribir tu contraseña."
    >
      <ul className="divide-y divide-border">
        {PROVIDERS.map((p) => {
          const acc = linked[p.id]
          return (
            <li key={p.id} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-bg-app">
                {p.icon}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-fg-primary">{p.name}</p>
                {acc ? (
                  <p className="truncate text-xs text-fg-muted">
                    {acc.provider_email ?? '—'} · vinculado{' '}
                    {new Date(acc.linked_at).toLocaleDateString('es-CO')}
                  </p>
                ) : (
                  <p className="text-xs text-fg-muted">Sin conectar</p>
                )}
              </div>
              {acc ? (
                confirming === p.id ? (
                  <div className="flex items-center gap-2">
                    <ActionButton variant="ghost" onClick={() => setConfirming(null)}>
                      Cancelar
                    </ActionButton>
                    <ActionButton
                      variant="danger"
                      onClick={() => onDisconnect(p.id)}
                      loading={disconnect.isPending}
                      disabled={!canDisconnect(p.id)}
                    >
                      Confirmar desconexión
                    </ActionButton>
                  </div>
                ) : (
                  <ActionButton
                    variant="secondary"
                    onClick={() => setConfirming(p.id)}
                    disabled={!canDisconnect(p.id)}
                    title={
                      !canDisconnect(p.id)
                        ? 'No puedes desconectar tu único método de acceso'
                        : undefined
                    }
                  >
                    Desconectar
                  </ActionButton>
                )
              ) : (
                <ActionButton variant="secondary" onClick={() => startOAuth(p.id, 'login')}>
                  Conectar
                </ActionButton>
              )}
            </li>
          )
        })}
      </ul>
      {!profile.has_password && totalLinked === 1 && (
        <div className="mt-4">
          <InlineAlert variant="info">
            Tu cuenta no tiene contraseña configurada. Mantén al menos una conexión OAuth
            para poder iniciar sesión.
          </InlineAlert>
        </div>
      )}
    </SettingsSection>
  )
}

function labelOf(id: 'google' | 'microsoft') {
  return id === 'google' ? 'Google' : 'Microsoft'
}
